'use client';

import { c as _c } from "react/compiler-runtime";
import type { CodeChangesResponse } from '@/lib/pr-v2/code-changes/types';
import type { PrV2FileDescriptionResponse } from '@/lib/pr-v2/file-review-decisions';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useSetPrV2GroupCardSlot } from '../../providers/pr-v2-sidebar-provider';
import { mapPrV2SummaryToPullRequest } from '../shared/pr-v2-pull-request-adapter';
import { PrV2SidebarRightSkeleton } from '../shared/skeletons/pr-v2-loading-sections';
import { buildAiContext } from '@/components/chat/build-ai-context';
import { SidebarChat } from '@/components/chat/sidebar-chat';
import { Sidebar, SidebarContent, SidebarRail, useSidebar } from '@/components/ui/sidebar';
import { BetterTooltip } from '@/components/ui/tooltip';
import { usePrefetchPageChatEngine } from '@/data/chat/page-chat-session';
import { prV2CodeChangesQueryKeyPrefix, prV2FileDescriptionQueryKeyPrefix } from '@/data/v2/pr/query-keys';
import { usePrV2CodeChanges } from '@/data/v2/pr/use-pr-v2-code-changes';
import { usePrV2FileDescription } from '@/data/v2/pr/use-pr-v2-file-description';
import { usePrV2Summary } from '@/data/v2/pr/use-pr-v2-summary';
import { useChatStore } from '@/lib/stores/chat-store';
import { getPrV2ActiveFileStoreKey, usePrV2ActiveFileStore } from '@/lib/stores/navigation/pr-v2-active-file-store';
import { usePrV2ContentPainted } from '@/lib/stores/navigation/pr-v2-content-paint-store';
import { usePrStore } from '@/lib/stores/pr-store';
import { PR_V2_CHAT_SIDEBAR_MOBILE_WIDTH } from '@/lib/stores/pr-v2-view-preferences';
function queryKeyStartsWith(queryKey: QueryKey, prefix: readonly unknown[]) {
  if (queryKey.length < prefix.length) {
    return false;
  }
  return prefix.every((part, index) => Object.is(queryKey[index], part));
}
function getQueryKeyComparisonShas(queryKey: QueryKey) {
  const comparisonPart = queryKey.at(-1);
  if (!comparisonPart || typeof comparisonPart !== 'object' || Array.isArray(comparisonPart)) {
    return {
      baseSha: null,
      headSha: null
    };
  }
  const headSha = (comparisonPart as {
    headSha?: unknown;
  }).headSha;
  const baseSha = (comparisonPart as {
    baseSha?: unknown;
  }).baseSha;
  return {
    baseSha: typeof baseSha === 'string' ? baseSha : null,
    headSha: typeof headSha === 'string' ? headSha : null
  };
}
function getLatestCachedQueryData<T>(queryClient: QueryClient, queryKeyPrefix: readonly unknown[], headSha?: string | null, baseSha?: string | null) {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: queryKeyPrefix
  });
  let latestData: T | null = null;
  let latestUpdatedAt = -1;
  for (const query of queries) {
    const data = query.state.data as T | undefined;
    if (!data) {
      continue;
    }
    const comparisonShas = getQueryKeyComparisonShas(query.queryKey);
    if (headSha && comparisonShas.headSha !== headSha) {
      continue;
    }
    if (baseSha && comparisonShas.baseSha !== baseSha) {
      continue;
    }
    if (query.state.dataUpdatedAt >= latestUpdatedAt) {
      latestData = data;
      latestUpdatedAt = query.state.dataUpdatedAt;
    }
  }
  return latestData;
}
function useLatestCachedQueryData(queryKeyPrefix, headSha, baseSha) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient || $[1] !== queryKeyPrefix) {
    t0 = onStoreChange => queryClient.getQueryCache().subscribe(event => {
      if (event.query && queryKeyStartsWith(event.query.queryKey, queryKeyPrefix)) {
        onStoreChange();
      }
    });
    $[0] = queryClient;
    $[1] = queryKeyPrefix;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const subscribe = t0;
  let t1;
  if ($[3] !== baseSha || $[4] !== headSha || $[5] !== queryClient || $[6] !== queryKeyPrefix) {
    t1 = () => getLatestCachedQueryData(queryClient, queryKeyPrefix, headSha, baseSha);
    $[3] = baseSha;
    $[4] = headSha;
    $[5] = queryClient;
    $[6] = queryKeyPrefix;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const getSnapshot = t1;
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// The diff portals the current group's card here. A closed desktop column stays mounted off
// screen and a phone has no room for it, so the slot exists only while the column is open on
// desktop. Without it the diff shows a guide beside each group.
function PrV2GroupCardSlot() {
  const $ = _c(2);
  const setGroupCardSlot = useSetPrV2GroupCardSlot();
  const {
    isMobile,
    open
  } = useSidebar();
  if (isMobile || !open) {
    return null;
  }
  let t0;
  if ($[0] !== setGroupCardSlot) {
    t0 = <div ref={setGroupCardSlot} data-testid="pr-v2-group-card-slot" className="shrink-0 border-b border-border p-3 empty:hidden" />;
    $[0] = setGroupCardSlot;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function PrV2SidebarRight() {
  const $ = _c(52);
  const {
    owner: storeOwner,
    repo: storeRepo,
    number: storeNumber,
    isInitialized: isPrStoreInitialized
  } = usePrStore();
  const isPrStoreReady = isPrStoreInitialized && storeOwner !== "" && storeRepo !== "" && storeNumber !== "";
  const owner = isPrStoreReady ? storeOwner : "";
  const repo = isPrStoreReady ? storeRepo : "";
  const number = isPrStoreReady ? storeNumber : "";
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = getPrV2ActiveFileStoreKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const prKey = t0;
  let t1;
  if ($[4] !== prKey) {
    t1 = state => state.activeFilenameByPr[prKey];
    $[4] = prKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const activeFilename = usePrV2ActiveFileStore(t1);
  const {
    isMobile,
    open,
    openMobile
  } = useSidebar();
  const chatId = useChatStore(_temp);
  const isChatPersisted = useChatStore(_temp2);
  const isSidebarOpen = isMobile ? openMobile : open;
  const shouldLoadChatContext = Boolean(isPrStoreReady && chatId && isSidebarOpen);
  usePrefetchPageChatEngine(isSidebarOpen);
  let t2;
  if ($[6] !== shouldLoadChatContext) {
    t2 = {
      enabled: shouldLoadChatContext
    };
    $[6] = shouldLoadChatContext;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const summaryQuery = usePrV2Summary(owner, repo, number, t2);
  const summary = isPrStoreReady ? summaryQuery.data : null;
  const summaryHeadSha = summary?.pullRequest.headSha ?? null;
  const summaryBaseSha = summary?.pullRequest.baseSha ?? null;
  let t3;
  if ($[8] !== number || $[9] !== owner || $[10] !== repo) {
    t3 = prV2CodeChangesQueryKeyPrefix(owner, repo, number);
    $[8] = number;
    $[9] = owner;
    $[10] = repo;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const codeChangesQueryKeyPrefix = t3;
  let t4;
  if ($[12] !== number || $[13] !== owner || $[14] !== repo) {
    t4 = prV2FileDescriptionQueryKeyPrefix(owner, repo, number);
    $[12] = number;
    $[13] = owner;
    $[14] = repo;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const fileDescriptionQueryKeyPrefix = t4;
  const cachedCodeChanges = useLatestCachedQueryData(codeChangesQueryKeyPrefix, summaryHeadSha, summaryBaseSha);
  const cachedFileDescription = useLatestCachedQueryData(fileDescriptionQueryKeyPrefix, summaryHeadSha, summaryBaseSha);
  const shouldFetchChatCodeContext = Boolean(shouldLoadChatContext && summaryHeadSha && summaryBaseSha && !cachedCodeChanges);
  let t5;
  if ($[16] !== shouldFetchChatCodeContext || $[17] !== summaryBaseSha || $[18] !== summaryHeadSha) {
    t5 = {
      headSha: summaryHeadSha,
      baseSha: summaryBaseSha,
      enabled: shouldFetchChatCodeContext
    };
    $[16] = shouldFetchChatCodeContext;
    $[17] = summaryBaseSha;
    $[18] = summaryHeadSha;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  const codeChangesQuery = usePrV2CodeChanges(owner, repo, number, t5);
  const shouldFetchChatFileDescription = Boolean(shouldLoadChatContext && summaryHeadSha && summaryBaseSha && !cachedFileDescription);
  let t6;
  if ($[20] !== shouldFetchChatFileDescription || $[21] !== summaryBaseSha || $[22] !== summaryHeadSha) {
    t6 = {
      headSha: summaryHeadSha,
      baseSha: summaryBaseSha,
      enabled: shouldFetchChatFileDescription
    };
    $[20] = shouldFetchChatFileDescription;
    $[21] = summaryBaseSha;
    $[22] = summaryHeadSha;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  const fileDescriptionQuery = usePrV2FileDescription(owner, repo, number, t6);
  const codeChanges = cachedCodeChanges ?? codeChangesQuery.data;
  const fileDescription = cachedFileDescription ?? fileDescriptionQuery.data;
  const reviewDecisions = fileDescription?.status === "ready" ? fileDescription.decisions : undefined;
  const isLoadingSummary = shouldLoadChatContext && summaryQuery.isLoading && !summary;
  const isLoadingCodeContext = shouldLoadChatContext && shouldFetchChatCodeContext && codeChangesQuery.isLoading && !codeChanges;
  let t7;
  if ($[24] !== activeFilename || $[25] !== codeChanges?.codeContent || $[26] !== codeChanges?.files || $[27] !== codeChanges?.headSha || $[28] !== codeChanges?.orderedFiles || $[29] !== owner || $[30] !== repo || $[31] !== reviewDecisions || $[32] !== summary) {
    t7 = summary ? buildAiContext({
      pr: mapPrV2SummaryToPullRequest(summary.pullRequest, owner, repo),
      codeContent: codeChanges?.codeContent,
      files: codeChanges?.files,
      headSha: codeChanges?.headSha ?? summary.pullRequest.headSha,
      orderedFiles: codeChanges?.orderedFiles,
      reviewDecisions,
      activeFilename
    }) : null;
    $[24] = activeFilename;
    $[25] = codeChanges?.codeContent;
    $[26] = codeChanges?.files;
    $[27] = codeChanges?.headSha;
    $[28] = codeChanges?.orderedFiles;
    $[29] = owner;
    $[30] = repo;
    $[31] = reviewDecisions;
    $[32] = summary;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  const aiContext = t7;
  const shouldRenderChat = Boolean(!isLoadingSummary && chatId && aiContext);
  const hasContentPainted = usePrV2ContentPainted(prKey);
  let t8;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PrV2GroupCardSlot />;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== isLoadingSummary || $[36] !== isPrStoreReady) {
    t9 = !isPrStoreReady || isLoadingSummary ? <PrV2SidebarRightSkeleton /> : null;
    $[35] = isLoadingSummary;
    $[36] = isPrStoreReady;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== aiContext || $[39] !== chatId || $[40] !== hasContentPainted || $[41] !== isChatPersisted || $[42] !== isLoadingCodeContext || $[43] !== isSidebarOpen || $[44] !== shouldRenderChat) {
    t10 = shouldRenderChat && chatId && aiContext ? <SidebarChat key={chatId} id={chatId} isPersisted={isChatPersisted} aiContext={aiContext} warmEnabled={hasContentPainted} enabled={isSidebarOpen} contextReady={!isLoadingCodeContext} /> : null;
    $[38] = aiContext;
    $[39] = chatId;
    $[40] = hasContentPainted;
    $[41] = isChatPersisted;
    $[42] = isLoadingCodeContext;
    $[43] = isSidebarOpen;
    $[44] = shouldRenderChat;
    $[45] = t10;
  } else {
    t10 = $[45];
  }
  let t11;
  if ($[46] !== t10 || $[47] !== t9) {
    t11 = <SidebarContent className="gap-0 overflow-hidden bg-background">{t8}<div className="relative mt-0 flex min-h-0 flex-1 flex-col overflow-hidden bg-background"><div className="relative flex min-h-0 min-w-0 flex-1">{t9}{t10}</div></div></SidebarContent>;
    $[46] = t10;
    $[47] = t9;
    $[48] = t11;
  } else {
    t11 = $[48];
  }
  let t12;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <BetterTooltip content="Resize sidebar"><SidebarRail disableToggle={true} /></BetterTooltip>;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== t11) {
    t13 = <Sidebar className={"border-r-border bg-background group-data-[side=left]:border-r *:data-[sidebar=\"sidebar\"]:bg-background"} side="left" mobileSide="right" mobileWidth={PR_V2_CHAT_SIDEBAR_MOBILE_WIDTH} offsetDesktopHeader={true} id="sidebar-right">{t11}{t12}</Sidebar>;
    $[50] = t11;
    $[51] = t13;
  } else {
    t13 = $[51];
  }
  return t13;
}
function _temp2(state_1) {
  return state_1.isChatPersisted;
}
function _temp(state_0) {
  return state_0.chatId;
}